import type React from 'react'
import { useEffect, useState } from 'react'
import DefaultModal from '../../../../../components/DefaultModal'
import FormButton from '../../../../../components/FormButton'
import { COUNTRY_OF_RESIDENCE_OPTIONS } from '../../../../../utils/complianceQuestionnaireOptions'

interface KycResidenceModalProps {
  isOpen: boolean
  onClose: () => void
  /**
   * Submit the declared residence to the pre-KYC gate. Resolves with the outcome so the modal can react:
   * 'eligible' → the parent closes this modal and continues to KYC; 'blocked' → the country is not supported
   * (a recoverable soft block, shown below); 'error' → a transient failure the parent has already surfaced.
   */
  onSubmit: (residenceIso: string) => Promise<'eligible' | 'blocked' | 'error'>
  isLoading: boolean
}

/**
 * The pre-KYC country gate step: the customer declares their country of residence before identity verification
 * starts. The server checks it against the Country Risk Register — refusing an unsupported country before any
 * KYC spend — and uses it to pick the right verification method (Non-Doc vs document). An unsupported country
 * is a *soft* block: the customer isn't frozen (the declaration is unverified) and can simply pick again.
 */
const KycResidenceModal: React.FC<KycResidenceModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
}) => {
  const [residence, setResidence] = useState('')
  const [blocked, setBlocked] = useState(false)

  // Clear the selection and any block message whenever the modal is reopened.
  useEffect(() => {
    if (!isOpen) {
      setResidence('')
      setBlocked(false)
    }
  }, [isOpen])

  const handleContinue = async () => {
    setBlocked(false)
    const outcome = await onSubmit(residence)
    if (outcome === 'blocked') setBlocked(true)
  }

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={onClose}
      isLoading={isLoading}
      inlineLoading
      title="Your country of residence"
      maxW="480px"
    >
      <div className="p-8 space-y-5">
        <p className="text-description">
          Before we verify your identity, please tell us where you live. We use
          this to confirm we can offer our services in your country and to choose
          the right verification method — so you don't start a check we can't
          complete.
        </p>

        <div>
          <label
            className="block text-sm font-medium mb-2"
            htmlFor="kyc-residence"
          >
            Country of residence
          </label>
          <select
            id="kyc-residence"
            className="input cursor-pointer"
            value={residence}
            onChange={e => {
              setResidence(e.target.value)
              setBlocked(false)
            }}
          >
            <option value="">Select a country</option>
            {COUNTRY_OF_RESIDENCE_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {blocked && (
          <div className="rounded-md bg-bg-component p-4 text-sm text-brand-light-red">
            We're sorry — we're not able to offer our services in the country you
            selected. If you picked the wrong country, choose another above.
            Otherwise, please contact{' '}
            <a
              href="mailto:support@penning.io"
              className="hover:underline font-medium"
            >
              support@penning.io
            </a>
            .
          </div>
        )}

        <FormButton
          title="Continue"
          onClick={handleContinue}
          isLoading={isLoading}
          loadingText="Checking…"
          disabled={!residence}
        />
      </div>
    </DefaultModal>
  )
}

export default KycResidenceModal
